(() => {
  'use strict';

  const roots = document.querySelectorAll('[data-f2w-founder-directory]');
  if (!roots.length) return;

  const normalize = (value) => String(value || '')
    .toLocaleLowerCase('nl-NL')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim();

  const parseList = (value) => {
    if (value === null || value === undefined) return [];
    if (Array.isArray(value)) return value.flatMap(parseList).filter(Boolean);
    const text = String(value).trim();
    if (!text) return [];
    if ((text.startsWith('[') && text.endsWith(']')) || (text.startsWith('{') && text.endsWith('}'))) {
      try {
        const parsed = JSON.parse(text);
        if (Array.isArray(parsed)) return parsed.flatMap(parseList).filter(Boolean);
        if (parsed && typeof parsed === 'object') return Object.values(parsed).flatMap(parseList).filter(Boolean);
      } catch (error) { /* Plain text fallback. */ }
    }
    return text.split(/[|,\n]+/).map((item) => item.trim()).filter(Boolean);
  };

  const richTextToPlainText = (value) => {
    const walk = (node) => {
      if (node === null || node === undefined) return '';
      if (typeof node === 'string') return node;
      if (Array.isArray(node)) return node.map(walk).join(' ');
      if (typeof node !== 'object') return '';
      if (typeof node.value === 'string') return node.value;
      return walk(node.children || node.content || Object.values(node));
    };

    try {
      return walk(JSON.parse(value))
        .replace(/<[^>]*>/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
    } catch (error) {
      return '';
    }
  };

  const fieldText = (field) => {
    const raw = String(field?.value || '').trim();
    if (!raw) return '';
    const parsed = raw.startsWith('{') || raw.startsWith('[') ? richTextToPlainText(raw) : '';
    return (parsed || raw)
      .replace(/<[^>]*>/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  };

  const idsFrom = (field) => parseList(field?.value)
    .filter((value) => String(value).startsWith('gid://shopify/Metaobject/'));

  const unique = (values) => [...new Set(values.map((value) => String(value || '').trim()).filter(Boolean))];
  const chunks = (values, size) => {
    const result = [];
    for (let index = 0; index < values.length; index += size) result.push(values.slice(index, index + size));
    return result;
  };

  const nativeUrl = (value) => {
    if (!value) return '';
    try {
      const url = new URL(String(value), window.location.origin);
      if (!['http:', 'https:'].includes(url.protocol)) return '';
      const root = window.Shopify?.routes?.root || '/';
      const prefix = root === '/' ? '' : root.replace(/\/$/, '');
      let path = url.pathname;
      if (/^\/[a-z]{2}(\/|$)/i.test(path)) path = path.replace(/^\/[a-z]{2}/i, '');
      if (prefix && path.startsWith(`${prefix}/`)) path = path.slice(prefix.length);
      return `${prefix}${path}${url.search}${url.hash}`;
    } catch (error) {
      return '';
    }
  };

  const shuffle = (items) => {
    const result = [...items];
    for (let index = result.length - 1; index > 0; index -= 1) {
      const randomIndex = Math.floor(Math.random() * (index + 1));
      [result[index], result[randomIndex]] = [result[randomIndex], result[index]];
    }
    return result;
  };

  const element = (tag, className, text) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined && text !== null) node.textContent = text;
    return node;
  };
  const createCountryFlag = (code, label) => {
    const flagCode = String(code || '').trim().toLowerCase();
    const isNation = ['gb-sct', 'gb-eng', 'gb-wls'].includes(flagCode);
    if (!flagCode || (flagCode.length !== 2 && !isNation)) return null;
    const wrapper = element('span', 'f2w-country-flag');
    const image = document.createElement('img');
    image.src = `https://flagcdn.com/w40/${flagCode}.png`;
    image.srcset = `https://flagcdn.com/w80/${flagCode}.png 2x`;
    image.alt = String(label || '');
    image.width = 20;
    image.height = 15;
    image.loading = 'lazy';
    image.decoding = 'async';
    wrapper.appendChild(image);
    wrapper.appendChild(document.createTextNode('\u00a0'));
    return wrapper;
  };

  const request = async (endpoint, token, query, variables) => {
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': token
      },
      body: JSON.stringify({ query, variables })
    });
    if (!response.ok) throw new Error('Storefront request failed');
    const json = await response.json();
    if (Array.isArray(json.errors) && json.errors.length) throw new Error('Storefront query failed');
    return json.data || {};
  };

  const INDEX_LABEL_QUERY = `
    query F2WFounderLabels($ids: [ID!]!, $language: LanguageCode!) @inContext(language: $language) {
      nodes(ids: $ids) {
        ... on Metaobject {
          id
          handle
          onlineStoreUrl
          name: field(key: "name") { value }
          code: field(key: "code") { value }
        }
      }
    }
  `;

  const FOUNDER_QUERY = `
    query F2WFoundersByIds($ids: [ID!]!, $language: LanguageCode!) @inContext(language: $language) {
      nodes(ids: $ids) {
        ... on Metaobject {
          id
          handle
          onlineStoreUrl
          name: field(key: "name") { value }
          role: field(key: "role") { value }
          country: field(key: "country") { value }
          sports: field(key: "sports") { value }
          specialization: field(key: "specialization") { value }
          dateOfBirth: field(key: "date_of_birth") { value }
          dateOfDeath: field(key: "date_of_death") { value }
          historicalRole: field(key: "historische_rol") { value }
          traditionGroup: field(key: "traditiegroep") { value }
          relatedProfiles: field(key: "gerelateerde_profielen") { value }
          profileIntro: field(key: "profile_intro") { value }
          profileContent: field(key: "profile_content") { value }
          achievements: field(key: "achievements") { value }
          legacy: field(key: "legacy") { value }
          timeline: field(key: "timeline") { value }
          resolvedImageUrl: field(key: "resolved_image_url") { value }
        }
      }
    }
  `;

  const relationNodes = (field) => field?.references?.nodes || [];
  const relation = (node) => ({
    id: node?.id || '',
    handle: node?.handle || '',
    label: fieldText(node?.name) || node?.handle || '',
    code: fieldText(node?.code),
    url: nativeUrl(node?.onlineStoreUrl)
  });

  const parseIndex = (payload) => {
    if (!payload || payload.v !== 1 || !Number.isInteger(payload.count) || !Array.isArray(payload.rows) || payload.rows.length !== payload.count) {
      throw new Error('Invalid Founder index');
    }
    const seen = new Set();
    return payload.rows.map((row) => {
      const id = String(row?.[0] || '').trim();
      if (!id.startsWith('gid://shopify/Metaobject/') || seen.has(id)) throw new Error('Invalid Founder index row');
      seen.add(id);
      return {
        id,
        handle: String(row?.[1] || '').trim(),
        countryId: String(row?.[2] || '').trim(),
        sportIds: unique(Array.isArray(row?.[3]) ? row[3] : []),
        roles: parseList(row?.[5]).map(normalize),
        historicalRoles: parseList(row?.[6]).map(normalize),
        traditions: parseList(row?.[7]).map(normalize)
      };
    }).filter((record) => record.roles.includes('founder') || record.historicalRoles.includes('founder'));
  };

  const loadIndexPayload = async (root, indexUrl) => {
    const inline = root.querySelector('[data-f2w-index-json]');
    const raw = inline?.textContent?.trim() || '';
    if (raw && raw !== 'null') {
      let payload = JSON.parse(raw);
      if (typeof payload === 'string') payload = JSON.parse(payload);
      if (payload) return payload;
    }
    if (!indexUrl) throw new Error('Missing Founder loader configuration');
    const response = await fetch(indexUrl, { credentials: 'same-origin' });
    if (!response.ok) throw new Error('Founder index request failed');
    return response.json();
  };

  roots.forEach((root) => {
    if (root.dataset.f2wInitialized === 'true') return;
    root.dataset.f2wInitialized = 'true';

    const token = root.dataset.storefrontToken || '';
    const language = String(root.dataset.language || document.documentElement?.lang || 'nl').split('-')[0].toUpperCase() || 'NL';
    const shop = root.dataset.shopDomain || window.Shopify?.shop || window.location.hostname;
    const apiVersion = root.dataset.apiVersion || '2024-10';
    const endpoint = root.dataset.storefrontEndpoint || `https://${shop}/api/${apiVersion}/graphql.json`;
    const indexUrl = root.dataset.indexUrl || '';
    const grid = root.querySelector('[data-f2w-founder-results]');
    const loading = root.querySelector('[data-f2w-founder-loading]');
    const errorElement = root.querySelector('[data-f2w-founder-error]');
    const empty = root.querySelector('[data-f2w-founder-empty]');
    const count = root.querySelector('[data-f2w-founder-results-count]');
    const pagination = root.querySelector('[data-f2w-founder-pagination]');
    const perPage = 24;
    let currentPage = 1;
    const countFormatter = new Intl.NumberFormat(root.dataset.language || document.documentElement?.lang || 'nl');
    root.querySelectorAll('[data-f2w-founder-snapshot-value]').forEach((value) => {
      const number = Number(value.textContent.trim());
      if (Number.isFinite(number)) value.textContent = countFormatter.format(number);
    });
    const search = root.querySelector('[data-f2w-founder-filter-search]');
    const countrySelect = root.querySelector('[data-f2w-founder-filter-country]');
    const sportSelect = root.querySelector('[data-f2w-founder-filter-sport]');
    const filterContainer = root.querySelector('[data-f2w-directory-filters]');
    const labels = {
      results: root.dataset.labelResults || '',
      loading: root.dataset.labelLoading || '',
      error: root.dataset.labelError || '',
      empty: root.dataset.labelEmpty || '',
      viewProfile: root.dataset.labelViewProfile || '',
      dictionary: (() => { try { return JSON.parse(root.dataset.labelDictionary || '{}'); } catch (e) { return {}; } })()
    };
    const editorial = [...root.querySelectorAll('[data-f2w-founder-editorial-section]')].map((section) => ({
      context: normalize(section.dataset.f2wFounderEditorialSection),
      section,
      grid: section.querySelector('[data-f2w-founder-editorial-grid]')
    }));

    let indexRecords = [];
    let metadata = new Map();
    let founders = [];
    let ready = false;
    let loadedKey = '';
    const selectionCache = new Map();

    const setElementVisibility = (node, visible) => {
      if (!node) return;
      node.hidden = !visible;
      node.style.setProperty('display', visible ? '' : 'none', 'important');
      node.setAttribute('aria-hidden', visible ? 'false' : 'true');
    };
    const setSearchVisible = (visible) => {
      const wrapper = search?.closest('.f2w-event-directory__filter');
      if (!wrapper) return;
      setElementVisibility(wrapper, visible);
      if (search) search.disabled = !visible;
    };
    const setLoading = (visible) => {
      if (!loading) return;
      setElementVisibility(loading, visible);
      loading.textContent = labels.loading;
    };
    const setError = (message) => {
      if (!errorElement) return;
      setElementVisibility(errorElement, Boolean(message));
      errorElement.textContent = message || '';
    };
    const labelFor = (id) => metadata.get(id)?.label || '';
    const codeFor = (id) => metadata.get(id)?.code || '';
    const keyFor = (value) => normalize(value).replace(/[\s-]+/g, '_');
    const dictionaryLabel = (group, key, fallback) => labels.dictionary?.[group]?.[key] || fallback;

    const loadMetadata = async (ids) => {
      const allIds = unique(ids);
      if (!allIds.length) return new Map();
      const pages = await Promise.all(chunks(allIds, 100).map((batch) => request(endpoint, token, INDEX_LABEL_QUERY, { ids: batch, language })));
      const result = new Map();
      pages.flatMap((page) => page.nodes || []).forEach((node) => {
        if (node?.id) result.set(node.id, { id: node.id, handle: node.handle || '', label: fieldText(node.name) || node.handle || '', code: fieldText(node.code), url: nativeUrl(node.onlineStoreUrl) });
      });
      return result;
    };

    const setOptions = (select, firstLabel, options, selected) => {
      if (!select) return;
      select.replaceChildren();
      const first = element('option', '', firstLabel);
      first.value = '';
      select.appendChild(first);
      options.forEach((option) => {
        const node = element('option', '', option.label);
        node.value = option.value;
        select.appendChild(node);
      });
      select.value = options.some((option) => option.value === selected) ? selected : '';
    };

    const optionValues = (records, key) => key === 'country'
      ? unique(records.map((record) => record.countryId))
      : unique(records.flatMap((record) => record.sportIds));

    const matchesIndexRecord = (record, current, except) => {
      if (except !== 'country' && current.country && record.countryId !== current.country) return false;
      if (except !== 'sport' && current.sport && !record.sportIds.includes(current.sport)) return false;
      return true;
    };

    const syncFilterOptions = () => {
      const current = filters();
      const configs = [
        { key: 'sport', select: sportSelect, label: root.dataset.allSportsLabel || '' },
        { key: 'country', select: countrySelect, label: root.dataset.allCountriesLabel || '' }
      ];

      configs.map((config) => {
        const options = optionValues(
          config.key === 'country'
            ? indexRecords
            : indexRecords.filter((record) => matchesIndexRecord(record, current, config.key)),
          config.key
        ).map((value) => ({
          value,
          label: labelFor(value) || value
        })).sort((a, b) => a.label.localeCompare(b.label, 'nl-NL'));
        return { ...config, options };
      }).forEach((config) => {
        setOptions(config.select, config.label, config.options, current[config.key]);
      });
      renderChips();
    };

    const buildFilters = () => {
      syncFilterOptions();
    };

    const mapFounder = (node, indexRecord) => {
      if (!node?.id || !indexRecord) return null;
      const countryId = indexRecord.countryId || idsFrom(node.country)[0];
      const sportIds = indexRecord.sportIds.length ? indexRecord.sportIds : idsFrom(node.sports);
      const relatedIds = idsFrom(node.relatedProfiles);
      const countryMeta = metadata.get(countryId);
      const sports = sportIds.map((id) => ({
        id,
        label: labelFor(id),
        url: nativeUrl(metadata.get(id)?.url)
      }));
      const relatedProfiles = relatedIds.map((id) => ({
        id,
        label: labelFor(id),
        url: nativeUrl(metadata.get(id)?.url)
      })).filter((profile) => profile.label);
      const roles = parseList(node.role?.value).filter((value) => !String(value).startsWith('gid://')).map((value) => ({ key: keyFor(value), label: dictionaryLabel('roles', keyFor(value), value) }));
      const historicalRoles = parseList(node.historicalRole?.value).filter((value) => !String(value).startsWith('gid://')).map((value) => ({ key: keyFor(value), label: dictionaryLabel('statuses', keyFor(value), value) }));
      const traditionValues = indexRecord.traditions.length
        ? indexRecord.traditions
        : parseList(node.traditionGroup?.value).filter((value) => !String(value).startsWith('gid://')).map(keyFor);
      const traditions = traditionValues.map((value) => ({
        key: keyFor(value),
        label: dictionaryLabel('traditions', keyFor(value), value)
      }));
      return {
        id: node.id,
        handle: node.handle || indexRecord.handle,
        url: nativeUrl(node.onlineStoreUrl),
        name: fieldText(node.name),
        roles,
        historicalRoles,
        traditions,
        country: countryId ? { id: countryId, label: countryMeta?.label || countryId, code: countryMeta?.code || '', url: nativeUrl(countryMeta?.url) } : null,
        countryId,
        sports,
        sportIds: sportIds.length ? sportIds : indexRecord.sportIds,
        relatedProfiles,
        specialization: fieldText(node.specialization),
        dateOfBirth: fieldText(node.dateOfBirth),
        dateOfDeath: fieldText(node.dateOfDeath),
        profileIntro: fieldText(node.profileIntro),
        profileContent: fieldText(node.profileContent),
        achievements: fieldText(node.achievements),
        legacy: fieldText(node.legacy),
        timeline: fieldText(node.timeline),
        image: fieldText(node.resolvedImageUrl)
      };
    };

    const createCard = (founder) => {
      const article = element('article', 'f2w-event-card');
      const link = element(founder.url ? 'a' : 'div', 'f2w-event-card__link');
      if (founder.url) link.href = founder.url;
      const imageWrapper = element('div', 'f2w-event-card__image');
      if (founder.image) {
        const image = document.createElement('img');
        image.src = founder.image;
        image.alt = founder.name;
        image.width = 900;
        image.loading = 'lazy';
        image.decoding = 'async';
        imageWrapper.appendChild(image);
      } else {
        const placeholder = element('div', 'f2w-event-card__image--placeholder');
        placeholder.appendChild(element('span', '', founder.name.slice(0, 1)));
        imageWrapper.appendChild(placeholder);
      }
      const body = element('div', 'f2w-event-card__body');
      const content = element('div', 'f2w-event-card__content');
      const sportLabels = founder.sports.map((sport) => sport.label).filter(Boolean);
      const countryLabel = founder.country?.label || '';
      if (sportLabels.length) {
        const topline = element('div', 'f2w-event-card__topline');
        sportLabels.forEach((sportLabel) => {
          topline.appendChild(element('span', 'f2w-event-card__period f2w-event-card__period--upcoming', sportLabel));
        });
        content.appendChild(topline);
      }
      content.appendChild(element('h2', 'f2w-event-card__title', founder.name));
      if (countryLabel) {
        const countryLine = element('p', 'f2w-event-card__location');
        const flag = createCountryFlag(founder.country?.code, countryLabel);
        if (flag) countryLine.appendChild(flag);
        countryLine.appendChild(document.createTextNode(countryLabel));
        content.appendChild(countryLine);
      }
      if (founder.specialization) content.appendChild(element('p', 'f2w-event-card__location', founder.specialization));
      const dates = [founder.dateOfBirth, founder.dateOfDeath].filter(Boolean);
      if (dates.length) content.appendChild(element('p', 'f2w-event-card__location', dates.join(' – ')));
      if (founder.url) content.appendChild(element('span', 'f2w-event-card__view', labels.viewProfile));
      body.appendChild(content);
      link.append(imageWrapper, body);
      article.appendChild(link);
      return article;
    };

    const filters = () => ({ search: search?.value || '', country: countrySelect?.value || '', sport: sportSelect?.value || '' });
    const chipShell = root.querySelector('[data-f2w-founder-chips]');
    const countryRow = root.querySelector('[data-f2w-founder-country-chips]');
    const sportRow = root.querySelector('[data-f2w-founder-sport-chips]');
    let countriesExpanded = false;
    let sportsExpanded = false;
    const renderChips = () => {
      if (!chipShell || !countryRow || !sportRow) return;
      const makeChip = (key, select, value, label) => {
        const button = element('button', 'f2w-event-card__sport', label);
        button.type = 'button';
        button.setAttribute('aria-pressed', String(select.value === value));
        if (key === 'country' && value) {
          const flag = createCountryFlag(codeFor(value), '');
          if (flag) { flag.setAttribute('aria-hidden', 'true'); flag.querySelector('img').alt = ''; button.prepend(flag); }
        }
        button.addEventListener('click', () => {
          select.value = value;
          select.dispatchEvent(new Event('change', { bubbles: true }));
        });
        return button;
      };
      const countryOptions = [...countrySelect.options].filter((option) => option.value).sort((a, b) => {
        const priority = (option) => ({ NL: 0, BE: 1 }[codeFor(option.value).toUpperCase()] ?? 2);
        return priority(a) - priority(b) || a.textContent.localeCompare(b.textContent, document.documentElement.lang || 'nl');
      });
      const selectedCountry = countryOptions.find((option) => option.value === countrySelect.value);
      const featuredCountries = countryOptions.slice(0, 6);
      if (selectedCountry && !featuredCountries.includes(selectedCountry)) featuredCountries.push(selectedCountry);
      const remainingCountries = countryOptions.filter((option) => !featuredCountries.includes(option));
      const countryButtons = [makeChip('country', countrySelect, '', root.dataset.allCountriesLabel || '')];
      for (const option of featuredCountries) countryButtons.push(makeChip('country', countrySelect, option.value, option.textContent.trim()));
      if (countriesExpanded) {
        for (const option of remainingCountries) countryButtons.push(makeChip('country', countrySelect, option.value, option.textContent.trim()));
      }
      if (remainingCountries.length) {
        const toggle = element('button', 'f2w-event-card__sport', countriesExpanded ? root.dataset.countryLess : root.dataset.countryMore);
        toggle.type = 'button';
        toggle.setAttribute('aria-expanded', String(countriesExpanded));
        toggle.addEventListener('click', () => {
          countriesExpanded = !countriesExpanded;
          renderChips();
          countryRow.querySelector('[aria-expanded]')?.focus({ preventScroll: true });
        });
        countryButtons.push(toggle);
      }
      countryRow.replaceChildren(...countryButtons);
      const sportOptions = [...sportSelect.options].filter((option) => option.value);
      const selectedSport = sportOptions.find((option) => option.value === sportSelect.value);
      const featuredSports = sportOptions.slice(0, 6);
      if (selectedSport && !featuredSports.includes(selectedSport)) featuredSports.push(selectedSport);
      const remainingSports = sportOptions.filter((option) => !featuredSports.includes(option));
      const sportButtons = [makeChip('sport', sportSelect, '', root.dataset.allSportsLabel || '')];
      for (const option of featuredSports) sportButtons.push(makeChip('sport', sportSelect, option.value, option.textContent.trim()));
      if (sportsExpanded) {
        for (const option of remainingSports) sportButtons.push(makeChip('sport', sportSelect, option.value, option.textContent.trim()));
      }
      if (remainingSports.length) {
        const toggle = element('button', 'f2w-event-card__sport', sportsExpanded ? root.dataset.sportLess : root.dataset.sportMore);
        toggle.type = 'button';
        toggle.setAttribute('aria-expanded', String(sportsExpanded));
        toggle.addEventListener('click', () => {
          sportsExpanded = !sportsExpanded;
          renderChips();
          sportRow.querySelector('[aria-expanded]')?.focus({ preventScroll: true });
        });
        sportButtons.push(toggle);
      }
      sportRow.replaceChildren(...sportButtons);
      chipShell.hidden = false;
      root.dataset.chipsReady = 'true';
    };
    const filtered = () => {
      const current = filters();
      const query = normalize(current.search);
      return founders.filter((founder) => {
        const searchable = normalize([founder.name, founder.specialization, founder.country?.label, ...founder.sports.map((item) => item.label), ...founder.traditions.map((item) => item.label), ...founder.roles.map((item) => item.label), ...founder.historicalRoles.map((item) => item.label)].join(' '));
        return (!query || searchable.includes(query)) && (!current.country || founder.countryId === current.country) && (!current.sport || founder.sportIds.includes(current.sport));
      });
    };

    const renderPagination = (totalResults) => {
      if (!pagination) return;
      const totalPages = Math.ceil(totalResults / perPage);
      pagination.hidden = totalPages <= 1;
      if (pagination.hidden) { pagination.replaceChildren(); return; }
      const go = (target) => {
        currentPage = Math.min(Math.max(target, 1), totalPages);
        renderDatabase();
        writeUrl();
        pagination.querySelector('[aria-current="page"]')?.focus({ preventScroll: true });
        grid?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      };
      const button = (text, target, active, ariaLabel = '') => {
        const node = element('button', 'f2w-event-directory__tab', text);
        node.type = 'button';
        node.setAttribute('aria-label', ariaLabel || `${root.dataset.labelPage || 'Pagina'} ${target}`);
        if (active) node.setAttribute('aria-current', 'page');
        else node.addEventListener('click', () => go(target));
        return node;
      };
      const controls = [];
      if (currentPage > 1) controls.push(button('◀', currentPage - 1, false, root.dataset.labelPrevious || 'Vorige'));
      const pages = [...new Set([1, currentPage - 1, currentPage, currentPage + 1, totalPages]
        .filter((number) => number >= 1 && number <= totalPages))].sort((a, b) => a - b);
      let previous = 0;
      for (const number of pages) {
        if (number - previous > 1) controls.push(element('span', '', '…'));
        controls.push(button(String(number), number, number === currentPage));
        previous = number;
      }
      if (currentPage < totalPages) controls.push(button('▶', currentPage + 1, false, root.dataset.labelNext || 'Volgende'));
      pagination.replaceChildren(...controls);
    };
    const renderDatabase = () => {
      const current = filters();
      const available = founders.filter((founder) =>
        (!current.country || founder.countryId === current.country) &&
        (!current.sport || founder.sportIds.includes(current.sport))
      ).length;
      if (filterContainer) filterContainer.hidden = !(available > 48 || current.search.trim());
      const matches = filtered().sort((a, b) => a.name.localeCompare(b.name, 'nl-NL'));
      const totalPages = Math.max(1, Math.ceil(matches.length / perPage));
      currentPage = Math.min(Math.max(currentPage, 1), totalPages);
      grid?.replaceChildren(...matches.slice((currentPage - 1) * perPage, currentPage * perPage).map(createCard));
      renderPagination(matches.length);
      if (count) count.textContent = [countFormatter.format(matches.length), labels.results].filter(Boolean).join(' ');
      setElementVisibility(empty, matches.length === 0);
    };

    const renderEditorial = () => {
      editorial.forEach(({ context, section, grid: target }) => {
        if (!target) return;
        const matches = founders.filter((founder) => founder.traditions.some((tradition) => tradition.key === context));
        const cards = shuffle(matches).slice(0, 2);
        target.replaceChildren(...cards.map(createCard));
        section.hidden = cards.length === 0;
      });
    };

    const hydrateSelection = async () => {
      const key = 'all-founders';
      setLoading(true);
      if (selectionCache.has(key)) {
        founders = selectionCache.get(key).slice();
        loadedKey = key;
        setSearchVisible(true);
        renderEditorial();
        renderDatabase();
        setLoading(false);
        return;
      }
      try {
        const ids = indexRecords.map((record) => record.id);
        const pages = await Promise.all(chunks(ids, 100).map((batch) => request(endpoint, token, FOUNDER_QUERY, { ids: batch, language })));
        const nodes = pages.flatMap((page) => page.nodes || []).filter(Boolean);
        const relationIds = nodes.flatMap((node) => [
          ...idsFrom(node.country),
          ...idsFrom(node.sports),
          ...idsFrom(node.relatedProfiles)
        ]);
        metadata = new Map([
          ...metadata,
          ...(await loadMetadata(relationIds))
        ]);
        const byId = new Map(indexRecords.map((record) => [record.id, record]));
        const received = nodes.map((node) => mapFounder(node, byId.get(node?.id))).filter((founder) => founder?.name);
        if (received.length !== ids.length) throw new Error('Founder hydration was incomplete');
        founders = received;
        selectionCache.set(key, founders.slice());
        loadedKey = key;
        setSearchVisible(true);
        renderEditorial();
        renderDatabase();
      } catch (loadError) {
        setError(labels.error);
      } finally {
        setLoading(false);
      }
    };

    const writeUrl = () => {
      const current = filters();
      const url = new URL(window.location.href);
      Object.entries({ q: current.search.trim(), country: current.country, sport: current.sport, page: currentPage > 1 ? String(currentPage) : '' }).forEach(([key, value]) => value ? url.searchParams.set(key, value) : url.searchParams.delete(key));
      url.searchParams.delete('tradition');
      window.history.replaceState(window.history.state, '', url);
    };
    const decodeFilterValue = (value) => {
      let decoded = String(value || '');
      for (let attempt = 0; attempt < 2; attempt += 1) {
        try {
          const next = decodeURIComponent(decoded);
          if (next === decoded) break;
          decoded = next;
        } catch (error) {
          break;
        }
      }
      return decoded;
    };

    const applyUrl = () => {
      const params = new URLSearchParams(window.location.search);
      if (search) search.value = params.get('q') || '';
      if (countrySelect) countrySelect.value = decodeFilterValue(params.get('country'));
      if (sportSelect) sportSelect.value = decodeFilterValue(params.get('sport'));
      const page = Number(params.get('page'));
      currentPage = Number.isSafeInteger(page) && page > 0 ? page : 1;
      syncFilterOptions();
      if (params.has('tradition')) {
        const url = new URL(window.location.href);
        url.searchParams.delete('tradition');
        window.history.replaceState(window.history.state, '', url);
      }
    };

    search?.addEventListener('input', () => { currentPage = 1; renderDatabase(); writeUrl(); });
    [countrySelect, sportSelect].forEach((select) => select?.addEventListener('change', () => { currentPage = 1; syncFilterOptions(); renderDatabase(); writeUrl(); }));

    (async () => {
      setSearchVisible(false);
      setLoading(true);
      try {
        if (!token) throw new Error('Missing Founder loader configuration');
        indexRecords = parseIndex(await loadIndexPayload(root, indexUrl));
        metadata = await loadMetadata(indexRecords.flatMap((record) => [record.countryId, ...record.sportIds]));
        buildFilters();
        applyUrl();
        ready = true;
        await hydrateSelection();
      } catch (loadError) {
        setError(labels.error);
      } finally {
        setLoading(false);
      }
    })();
  });
})();



